import React, { useRef, useEffect, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { useReactToPrint } from 'react-to-print';
import Button from 'components/commercetools-ui/atoms/button';
import ShareProjectPopup from 'components/commercetools-ui/organisms/project-builder/share-project-popup';
import Spinner from 'components/commercetools-ui/organisms/spinner';
import Download from 'components/icons/download';
import Print from 'components/icons/print';
import Share from 'components/icons/share';
import {
  BOWL_POSITION,
  CAPACITY_SIZE,
  CAPACITY_TO_OVERFLOW,
  CARTRIDGE_SIZE,
  CARTRIDGE_TYPE,
  CLEAN_FLUSH,
  COATING_METHOD,
  COLOR,
  INDEPENDENT_LIVING_COMPLAINT,
  INLET_TYPE,
  ISAMBULANT,
  ISCOMPLIANT,
  ISNZCOMPLAINT,
  ITEM_CODE,
  ITEM_MATERIAL,
  ITEM_NAME,
  ITEM_TAPHOLES,
  LITRES_PER_MINUTE,
  MOUNT_TYPE,
  OUTLET_LENGTH,
  OUTLET_TYPE,
  OVERFLOW,
  PAN_HEIGHT,
  PAN_PROJECTION,
  PRODUCT_CODE,
  PRODUCT_TYPE,
  RAIL_LENGTH,
  RANGE,
  SHAPE,
  SHOWER_HEAD_SIZE,
  SPRAY_FUNCTIONS,
  SURFACE_FINISH,
  TAPHOLES,
  TRAP_TYPE,
  WASTE_POSITION,
  WELS_FLOWRATE_1,
  WELS_FLOWRATE_2,
  THUMBNAIL
} from 'helpers/constants/attributes';
import { localeMapping } from 'helpers/constants/localeMapping';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { useRouter } from 'next/navigation';
import { useAccount } from 'frontastic/hooks';
import Typography from 'components/commercetools-ui/atoms/typography';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Markdown from 'components/commercetools-ui/atoms/markdown';

const ProjectSummary: React.FC<any> = ({ projectFormData, handleTabClick, allowShare, data }: any) => {
  const {locale} = useParams();
  const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
   const [baseUrl, setBaseUrl] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const [isDesktopSize] = useMediaQuery(desktop);
  const { account } = useAccount();
  const imageUrl = data?.dataSource?.sections[0]?.section_with_banner?.banner_image.url;
  const { projectName, projectLocation, projectType, createdAt, roomGroups, showPricesOnPdf } =
    projectFormData;
  const formatDate = (date: string | undefined) => {
    return date
      ? new Date(date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : 'N/A';
  };

  const navigationToDiffStepper = (id: number) => {
    handleTabClick(id, projectFormData);
  };

  if (!projectFormData) {
    return <></>;
  }
  const handleShare = () => {
    setShowSharePopup(true);
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const reactToPrintFn = useReactToPrint({ contentRef: pageRef });
  async function generatePDFFromHTML() {
    if (!pageRef.current) return;
    try {
      setLoading(true);
      const pageHtml = pageRef.current.innerHTML;
  
      const response = await fetch(`/${locale}/api/pdf-my-html/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body:JSON.stringify({html:pageHtml}),
      });
  
      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.statusText}`);
      }
  
      const blob = await response.blob();
      const fileName = "Project_Summary.pdf";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 100);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }finally{
      setLoading(false);
  
    }
  }

  const buttons = [
    { text: 'Share', onClick: handleShare, icon: <Share /> },
    { text: 'Save', onClick: () => generatePDFFromHTML(), icon: <Download /> },
    { text: 'Print', onClick: () => reactToPrintFn(), icon: <Print /> },
  ];
  const tableData = {
    headers: ['Room', 'Product Code', 'Product Name', 'Qty'],
  };

  if (showPricesOnPdf) tableData.headers.push('RRP(inc GST)');

  const handleClose = () => {
    setShowSharePopup(false);
  };

  const totalQuantity = roomGroups
  ?.flatMap((room: { items: any[] }) => room.items?.filter((item:any)=> item.isProductAvailable)?.map((item: { quantity: number }) => item.quantity))
  .reduce((acc: number, amount: number) => acc + amount, 0) || 0;

const totalAmount = roomGroups
  ?.flatMap((room: { items: any[] }) =>
    room.items?.filter((item:any)=> item.isProductAvailable)?.map((item: { quantity: number; variant: { price: { centAmount: number } } }) =>
      item.quantity * item.variant.price.centAmount
    )
  )
  .reduce((acc: number, amount: number) => acc + amount, 0) || 0;


  const classNameDesktop = 'h-[80vh] w-full bg-cover bg-center flex lg:flex-row lg:justify-between';
  const classNameMob = 'w-full md:h-[52vh] print:h-[52vh] sm:h-[75vh] bg-cover bg-center flex justify-start flex-col overflow-hidden';
  const handleButtonClick = () => {
    window.location.href = '/';
  };
  const shareLink = `${baseUrl}/${locale}/project-builder/share-summary/?projectId=${projectFormData?.shoppingListId || ''}`;
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const basePath = url.origin;
      setBaseUrl(basePath);
    }
  }, []);

  return (
    <>
      <div className="p-20 print:p-0" ref={pageRef}>
        <div className="w-full">
          <div
            style={{ backgroundImage: `url(${imageUrl})` }}
            className={`product-summary print:shadow-none print:origin-start print:h-[90vh] flex break-after-page ${
              isDesktopSize ? classNameDesktop : classNameMob
            } `}
          >
            <div className=" space-y-2 print:space-y-[0px] p-[10px] text-white md:p-[20px] lg:p-[30px] summary-head-left">
              <h1 className="text-3xl font-semibold print:text-2xl">{projectName}</h1>
              <h2 className="py-10 print:py-[0px] text-lg font-light print:text-md">by {account?.companyName}</h2>
              {!allowShare && (
                <div className=" flex flex-row gap-8 print:hidden" data-html2canvas-ignore>
                  {buttons.map((button, index) => (
                    <button
                      key={index}
                      onClick={() => button?.onClick && button?.onClick()}
                      className="flex flex-row items-center justify-center gap-4 rounded-none bg-white p-8 text-black hover:bg-gray-500"
                    >
                      {button.icon}
                      {button.text}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-10 print:mt-0 p-[10px] text-white sm:w-full md:w-full md:p-[20px] print:w-full print:p-[20px] lg:w-[50%] print:w-[50%] lg:p-[30px]">
              <React.Fragment>
                <p className=" text-sm">Project Type</p>
                <p className=" text-2xl print:text-xl">{projectType.name}</p>
                <div className="my-2 border-b border-white" />
              </React.Fragment>

              <React.Fragment>
                <p className="text-sm">Location</p>
                <p className="text-2xl print:text-xl">{projectLocation.name}</p>
                <div className="my-2 border-b border-white" />
              </React.Fragment>

              <React.Fragment>
                <p className="text-sm">Date Created</p>
                <p className="text-2xl print:text-xl">{formatDate(createdAt)}</p>
                <div className="my-2 border-b border-white" />
              </React.Fragment>

              <React.Fragment>
                <p className="text-sm">Prepared by</p>
                <p className="text-2xl print:text-xl">{projectFormData?.username || '-'}</p>
                <div className="my-2 border-b border-white" />
              </React.Fragment>
            </div>
          </div>

          <div className="my-20 text-2xl print:text-xl font-semibold print:!my-[10px] "> Product Summary </div>

          <div className=" mt-4 break-after-page overflow-x-auto print:mt-[4px]">
            <table className="w-full border-collapse border border-black">
              <thead>
                <tr className="bg-gray-200">
                  {tableData.headers.map((header, index) => (
                    <th key={index} className="border border-black px-8 py-8 text-left print:text-[12px]">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='print:text-[12px]'>
                {roomGroups?.map((room: any, rowIndex: number) =>
                  room.items?.filter((item:any) => item.isProductAvailable)?.map((item: any, itemIndex: number) => (
                    <tr key={rowIndex}>
                      <td className="border border-black px-8 py-8">{itemIndex === 0 ? room.roomName : ''}</td>
                      <td className="border border-black px-8 py-8">{item.variant?.attributes?.['item-code']}</td>
                      <td className="border border-black px-8 py-8">{item.name}</td>
                      <td className="border border-black px-8 py-8">{item.quantity}</td>
                      {showPricesOnPdf && (
                        <td className="border border-black px-8 py-8">
                          {CurrencyHelpers.formatForCurrency( item.quantity * item.variant.price.centAmount, 'en', 'USD', 2).replace(
                            'US',
                            '',
                          )}
                        </td>
                      )}
                    </tr>
                  )),
                )}
                <tr>
                  <td className="border border-black px-8 py-8 text-right font-semibold" colSpan={3}>
                    Total
                  </td>
                  <td className="border border-black px-8 py-8">{totalQuantity}</td>
                  {showPricesOnPdf && (
                    <td className="border border-black px-8 py-8">
                      {CurrencyHelpers.formatForCurrency(totalAmount as number, 'en', 'USD', 2).replace('US', '')}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
          {roomGroups?.map((e: any, idx:any) => {
            return (
              <div key={idx} className=''>
                <h2 className="pt-20 print:pt-[10px] text-2xl print:text-lg font-medium">{e.roomName}</h2>
                {e.items?.filter((item:any)=> item.isProductAvailable)?.map((data: any, index: number) => (
                  <div key={index} className={`${!data.isProductAvailable ?"print:hidden":""} ${idx === roomGroups.length - 1 && index === e.items.length -1 ? "":"break-after-page"}`}>
                     {!data.isProductAvailable && ( 
                      <div className={` w-full shadow-none pt-20 print:!pt-0 print:hidden`}>
                        <div>
                          <div className="grid w-full justify-between p-8 print:!p-0 sm:flex">
                            <div className="mb-[12px] print:!mb-0 flex items-start space-x-2 print:pt-[10px]">
                              <InformationCircleIcon className="max-w-64 h-24 print:!w-[16px] print:!h-[16px] max-h-64  w-24 flex-shrink-0 stroke-[#F27320] text-[#F27320]" />
                              <Typography className="ml-[10px] text-[16px] text-[#F27320] print:!text-[12px] print:!pt-[0px]">
                                This product you have selected may no longer be available.
                              </Typography>
                            </div>
                            <Button onClick={() => handleButtonClick()} className="!bg-[#A6C9CC] text-black print:hidden">
                              View recommended alternatives
                            </Button>
                          </div>
                        </div>
                      </div>
                  )} 
                    <div className={`pt-20 ${index === 0 || !data.isProductAvailable ?"print:!pt-[0px]":"print:!pt-[10px]"} ${!data.isProductAvailable ? 'opacity-50 print:hidden' : ''}`}>
                      <ProductCard key={index} {...data} handleTabClick={handleTabClick} allowShare={allowShare} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {!allowShare && (
            <div className=" mt-5 flex justify-between print:hidden" data-html2canvas-ignore>
              <Button onClick={() => navigationToDiffStepper(2)}>Back to products</Button>
            </div>
          )}

          <ShareProjectPopup shareLink={shareLink} show={showSharePopup} onClose={handleClose} />
        </div>
      </div>
      {loading && <Spinner />}
    </>
  );
};

const ProductCard = ({ handleTabClick, allowShare, ...productDataProps }: any) => {
  const [addlAttr, setAddlAttr] = useState([]);
  const { locale } = useParams();
  const { roomNotes } = productDataProps;
  const { name, variant } = productDataProps;
  const { attributes, lineDiagramList, images } = variant;
  const itemAttributes = attributes?.['item-attributes'];
  const productCode = attributes?.['item-code'];
  const imageLeft = lineDiagramList?.[0] || THUMBNAIL;
  const imageRight = images?.[0] || THUMBNAIL;

  const router = useRouter();
  const displayAttributes: any = {};
  itemAttributes.forEach((e: any) => {
    displayAttributes[e.name] = e.value;
  });

  const localeKey = localeMapping[locale];

  const itemAttrs = variant?.attributes?.['item-attributes'];
  const requiredItemAttributes = [
    'depth',
    'length',
    'width',
    'net-weight',
    'IsAS14281Compliant',
    'IsAS14281Ambulant',
    'IsIndependentLivingCompliant',
    'IsNZ41121Compliant',
  ];
 
  const p: any = [];
  const getValues = () => {
    requiredItemAttributes.forEach((ra: string) => {
      const found = itemAttrs.filter((xx: any) => {
        return xx.name === ra;
      });
      // Checking attributes "IsAS14281Compliant", "IsAS14281Ambulant" & "IsNZ41121Compliant"
      // In PDP page, under Technical Information
      // If locale is NZ then we will show IsNZ41121Compliant   and   if locale is AU then we will show "IsAS14281Compliant" & "IsAS14281Ambulant"
      if (found && found.length > 0) {
        if (found[0].name === 'IsAS14281Compliant') {
          found[0].name = 'AS1428.1 Accessible';
        } else if (found[0].name === 'IsAS14281Ambulant') {
          found[0].name = 'AS1428.1 Ambulant';
        } else if (found[0].name === 'IsIndependentLivingCompliant') {
          found[0].name = 'Independent Living Compliant';
        } else if (found[0].name === 'IsNZ41121Compliant') {
          found[0].name = 'NZ412.1 Accessible';
        }
        if (ra !== 'IsNZ41121Compliant' && ra !== 'IsAS14281Compliant' && ra !== 'IsAS14281Ambulant') {
          p.push(found[0]);
        } else {
          if (locale === 'nz' && ra === 'IsNZ41121Compliant') {
            p.push(found[0]);
          }
          if (locale === 'au' && (ra === 'IsAS14281Compliant' || ra === 'IsAS14281Ambulant')) {
            p.push(found[0]);
          }
        }
      }
    });
  };
  useEffect(() => {
    p.length === 0 && getValues();
    setAddlAttr(p);
  }, []);

  const navigateProductDetails = () => {
    router.push(`${locale}/${productDataProps?.slug}`);
  };

  return (
    <>
      <b>{name}</b>
      <h4 className='print:text-[13px]'>Code: {productCode}</h4>
      <hr className="hor-rule-hr" />
      <div className="product-images flex flex-col gap-4 print:gap-2 md:flex-row print:flex-row">
        {imageRight &&
          <img src={imageRight} className="w-full md:w-1/2 print:w-1/2" alt="Right Image" />}
        {imageLeft &&
          <img src={imageLeft} className="w-full md:w-1/2  print:w-1/2" alt="Left Image" />}
      </div>
      <div className='hor-rule'>
      <h3 className="my-4 print:!my-[8px] print:text-[14px]">Notes</h3>
      <h4 className='print:text-[12px]'>{roomNotes}</h4>
      </div>
      <div className="mb-20 grid grid-cols-1 gap-4 print:gap-[40px] print:mb-[20px] md:grid-cols-2 print:grid-cols-2">
        <div>
          <h3 className="text-lg print:text-[14px] font-semibold">Attributes</h3>
          <div className="mt-32 flex flex-col gap-[10px] print:!mt-[0px] print:!gap-[4px]">
            {variant?.attributes &&
              [ITEM_CODE, PRODUCT_TYPE, RANGE, COLOR, ITEM_MATERIAL, COATING_METHOD].map((attr: any, index: number) => {
                const value = variant?.attributes?.[attr];
                if (
                  value !== 'Not Applicable' &&
                  value !== undefined &&
                  value !== '' &&
                  attr !== ITEM_NAME &&
                  !Array.isArray(value)
                ) {
                  return (
                    <>
                      <div key={index} className="flex text-14 print:text-[12px] print:justify-between print:gap-[2px] font-normal leading-6 lg:gap-0 lg:text-16">
                        <span className="flex-1 capitalize text-[#4D4D4D]">
                          {attr?.replaceAll('-', ' ').replace('gwa', '')}
                        </span>
                        <span className="ml-20 flex-1 print:flex-none print:!ml-[0px] text-[#090909]">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </span>
                      </div>

                      <div className="attribute-break-h"></div>
                    </>
                  );
                }
                return null;
              })}
            {variant?.attributes &&
              Object.entries(variant.attributes)
                .reverse()
                .filter(
                  ([key, value]) =>
                    value !== '' &&
                    value !== 'Not Applicable' &&
                    value !== 0 &&
                    key !== ITEM_NAME &&
                    !Array.isArray(value) &&
                    [
                      CAPACITY_SIZE,
                      ITEM_TAPHOLES,
                      TRAP_TYPE,
                      INLET_TYPE,
                      PAN_HEIGHT,
                      PAN_PROJECTION,
                      CLEAN_FLUSH,
                      MOUNT_TYPE,
                      OUTLET_TYPE,
                      OUTLET_LENGTH,
                      CARTRIDGE_SIZE,
                      CARTRIDGE_TYPE,
                      LITRES_PER_MINUTE,
                      SHAPE,
                      WASTE_POSITION,
                      WELS_FLOWRATE_1,
                      WELS_FLOWRATE_2,
                      SPRAY_FUNCTIONS,
                      RAIL_LENGTH,
                      SHOWER_HEAD_SIZE,
                      OVERFLOW,
                      TAPHOLES,
                      CAPACITY_TO_OVERFLOW,
                      SURFACE_FINISH,
                      BOWL_POSITION,
                      ISCOMPLIANT,
                      ISAMBULANT,
                      INDEPENDENT_LIVING_COMPLAINT,
                      ISNZCOMPLAINT,
                    ].includes(key),
                )
                .map(([key, value], index) => (
                  <div key={index} className='flex flex-col gap-10 print:gap-1'>
                    <div className="flex text-14 print:text-[12px] font-normal print:justify-between print:gap-[2px] leading-6 lg:gap-0 lg:text-16">
                      <span className="flex-1 capitalize text-[#4D4D4D]">
                        {key === CARTRIDGE_SIZE
                          ? 'Cartridge Size'
                          : key === CLEAN_FLUSH
                          ? 'Cleanflush'
                          : key.replaceAll('-', ' ').replace('gwa', '')}
                      </span>
                      <span className="ml-20 flex-1 print:flex-none print:ml-[0px] text-[#090909]">
                        {key === PAN_HEIGHT ||
                        key === PAN_PROJECTION ||
                        key === RAIL_LENGTH ||
                        key === OUTLET_LENGTH ||
                        key === SHOWER_HEAD_SIZE
                          ? `${value} mm`
                          : key === CAPACITY_SIZE
                          ? `${value} L`
                          : key === LITRES_PER_MINUTE
                          ? `${value} L /min`
                          : (key === WELS_FLOWRATE_1 || key === WELS_FLOWRATE_2) && typeof value === 'string'
                          ? value.replace('L/min', '  L / min')
                          : key === CARTRIDGE_SIZE && typeof value === 'string'
                          ? value.replace(/(\d)([a-zA-Z])/g, '$1 $2')
                          : typeof value === 'boolean'
                          ? value
                            ? 'Yes'
                            : 'No'
                          : typeof value === 'string' || typeof value === 'number'
                          ? value
                          : ''}
                      </span>
                    </div>
                      <div className="attribute-break-h"></div>
                    
                  </div>
                ))}

            {addlAttr
              ?.filter((attr: any) =>
                [ISCOMPLIANT, ISAMBULANT, INDEPENDENT_LIVING_COMPLAINT, ISNZCOMPLAINT].includes(attr.name),
              )
              .map((attr: any, index: number) => (
                <div key={index} className='flex flex-col gap-10 print:gap-[4px]'>
                  <div className="flex text-14 print:text-[12px] font-normal print:justify-between print:gap-[2px] leading-6 lg:gap-0 lg:text-16">
                    <span className="flex-1 capitalize text-[#4D4D4D]">
                      {attr.name.replaceAll('-', ' ').replace('gwa', '')} AA
                    </span>
                    <span className="ml-20 flex-1 print:flex-none print:ml-[0px]  text-[#090909]">
                      {typeof attr.value === 'boolean' ? (attr.value ? 'Yes' : 'No') : attr.value}
                    </span>
                  </div>
                      <div className="attribute-break-h"></div>
                  
                </div>
              ))}
          </div>
        </div>
        <div>
          <div>
            <h3 className="text-lg print:text-[14px] font-semibold ">Features & Benefits</h3>
            <div className="ml-[35px] print:ml-[16px] list-inside list-disc [&>ul]:list-disc print:text-[12px]">
              <Markdown className="text-black [&>ul]:list-disc ">
                {displayAttributes.features[localeKey].replace(
                  /(^|\n)-([^\`])/g,
                  '$1- $2'
                )}
              </Markdown>
            </div>
          </div>
          {!allowShare && productDataProps.isProductAvailable && (
            <div className=" mt-5 flex  justify-center print:hidden">
              <Button
                variant={'secondary'}
                className="px-8 py-8 text-sm font-medium"
                type="button"
                onClick={navigateProductDetails}
                data-html2canvas-ignore
              >
                View Product Detail page
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className=" product-price-and-quantity mb-[64px] print:mb-[0px]">
        <div className="mt-20 flex justify-between print:mt-[2px]">
          <span className="text-24 text-[#666666] print:text-[16px]">
            RRP: {CurrencyHelpers.formatForCurrency((productDataProps.quantity * variant?.price?.centAmount), 'en', 'USD', 2).replace('US', '')}
          </span>
          <span className="text-24 text-[#666666] print:text-[14px]">Qty {productDataProps.quantity}</span>
        </div>
        <div className="hor-rule-rrp" />
      </div>
    </>
  );
};

export default ProjectSummary;
